import { useEffect, useState } from "react";
import Link from "next/link";
import { cn } from "@/libs/utils";
import { useTrans } from "@/utils/useTrans";
import { Button } from "@/v2-components/ui/button";
import { Halloween2026HeroOverlay } from "@/v2-components/event/halloween-2026-hero-overlay";

/**
 * 홈 히어로 — 피그마 `hero`(28983:88468) + 디자이너 공유 HTML.
 *
 * 소재는 기존 홈 메인 배너와 같은 영상(기획 요청). 포스터 대신 `#t=0.1` 로 첫 프레임을 세운다.
 *
 * ⚠️ 높이를 `h-screen` 으로 두면 안 된다. 헤더(`PageTemplate`)가 `sticky top-0` 이라 **문서 흐름을
 * 차지**한다 — 100vh 를 그대로 주면 첫 화면에서 헤더 높이만큼 스크롤이 생긴다.
 *
 * ⚠️ 그렇다고 `100vh - 70px`(GNB 고정값)로 빼도 안 된다. 그 sticky 묶음에는 GNB 말고도
 * `SuggestLocale`·`ReferralBanner`·`PromoBanner` 가 조건부로 들어와서 높이가 70px 이 아니다.
 * (실측: 지역 제안 배너가 뜨면 136px → 히어로 하단 화살표가 화면 밖으로 밀린다)
 * 그래서 `[data-sticky-header]` 를 **실측해** CSS 변수로 넘긴다. 측정 전(SSR/첫 페인트)에는
 * 70px 로 폴백한다.
 *
 * 그 아래(<1024)는 시안대로 **고정 600px**. 모바일을 고정 px 로 두는 건 부작용이 아니라 이득이다 —
 * 모바일 브라우저의 100vh 는 주소창 접힘에 따라 흔들려서 히어로가 스크롤 중에 리사이즈된다.
 */

const VIDEO =
  "https://cdn.bhaptics.com/Banners/Main+Banner/main_video_small.mp4#t=0.1";

/** 헤더 스택 실측 전에 쓰는 폴백 = GNB 단독 높이(피그마 70px). */
const FALLBACK_HEADER_HEIGHT = 70;

function useStickyHeaderHeight() {
  const [height, setHeight] = useState(FALLBACK_HEADER_HEIGHT);

  useEffect(() => {
    const header = document.querySelector<HTMLElement>("[data-sticky-header]");
    if (!header) return;

    const measure = () =>
      setHeight(Math.round(header.getBoundingClientRect().height));
    measure();

    // ResizeObserver 가 없는 환경(jsdom 등)에서는 resize 만으로 떨어진다 —
    // 폴백 70px 로 굳어버리지 않게 최소한 한 번은 실측한다.
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", measure);
      return () => window.removeEventListener("resize", measure);
    }
    // 배너가 붙었다 떨어질 때마다 갱신된다(쿠키 배너·지역 제안 등).
    const observer = new ResizeObserver(measure);
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  return height;
}

export default function HomeHero({ className }: { className?: string }) {
  const { t } = useTrans(["index"]);
  const headerHeight = useStickyHeaderHeight();

  /**
   * 화살표 → 다음 섹션으로 **부드럽게** 이동.
   *
   * ⚠️ `html { scroll-behavior: smooth }` 를 전역으로 켜지 않는다. 그러면 체크아웃 단계 이동,
   * 에러 필드 포커스, 캐러셀의 `scrollBy` 까지 전부 애니메이션이 붙는다.
   *
   * ⚠️ 앵커 기본 동작은 섹션 상단을 뷰포트 상단에 맞추는데, 헤더가 `sticky` 라 그 자리를
   * 덮는다 — 제목이 헤더 뒤로 들어간다. 실측한 헤더 높이만큼 빼서 멈춘다.
   */
  const scrollToUsecase = (event: React.MouseEvent<HTMLAnchorElement>) => {
    // 새 탭/새 창으로 여는 조작은 가로채지 않는다.
    if (
      event.metaKey ||
      event.ctrlKey ||
      event.shiftKey ||
      event.button !== 0
    ) {
      return;
    }
    const target = document.getElementById("usecase");
    if (!target) return; // 섹션 id 가 바뀌면 기본 앵커 동작에 맡긴다

    event.preventDefault();
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    window.scrollTo({
      top:
        target.getBoundingClientRect().top + window.scrollY - headerHeight - 16,
      behavior: reduceMotion ? "auto" : "smooth",
    });
  };

  return (
    <section
      id="top"
      style={{ "--home-header-h": `${headerHeight}px` } as React.CSSProperties}
      className={cn(
        "relative h-[600px] overflow-hidden bg-coolgray-80",
        "laptop:h-[calc(100svh-var(--home-header-h,70px))] laptop:min-h-[640px]",
        className,
      )}
    >
      <video
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className="absolute inset-0 size-full object-cover"
      >
        <source src={VIDEO} type="video/mp4" />
      </video>

      {/* 피그마 hero-scrim(29078:22019) — 아래로 갈수록 짙어져 흰 문구를 받쳐준다.
          Tailwind 그라디언트 유틸은 정지점이 3개(from/via/to)뿐이라 4정지점을 표현할 수 없어
          인라인 스타일로 둔다. */}
      <div
        className="pointer-events-none absolute inset-0"
        style={{
          backgroundImage:
            "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.10) 40%, rgba(0,0,0,0.62) 78%, rgba(0,0,0,0.82) 100%)",
        }}
      />

      {/* 할로윈 2026 낙하 오버레이 — 배경 위 · 카피 아래(#6061) */}
      <Halloween2026HeroOverlay />

      {/* 시안: 650x262 블록이 히어로 바닥에서 10px 위(1440x720 기준 y=448). */}
      <div className="absolute bottom-0 left-1/2 flex w-[calc(100%-32px)] -translate-x-1/2 flex-col items-center gap-4 laptop:bottom-2.5 laptop:w-[650px] laptop:max-w-[calc(100%-32px)] laptop:gap-[60px]">
        <div className="flex w-full flex-col items-center gap-4 laptop:gap-8">
          <div className="flex flex-col items-center gap-4 text-center text-white">
            <h1 className="text-3xl font-400 laptop:text-6xl">
              {t("Feel the Game in Haptics")}
            </h1>
            {/* 색이 브레이크포인트마다 다르다 — 시안 확인 결과 **의도된 차이**다.
                모바일(29042:152253)은 본문만 한 톤 낮춰 `#d9dee3` 이고 제목은 순백이라 위계가
                생긴다. 데스크톱(28983:88478)은 `hero-content` 에 `text-white` 가 걸려 제목·본문이
                **둘 다 순백**이다.
                `#d9dee3` 은 우리 토큰에 없어 가장 가까운 `coolgray-20`(#DDE1E6, 채널당 3~4 차이)로
                둔다 — 육안 구분이 안 되는 차이라 토큰 밖 임의값을 만들지 않는다. */}
            <p className="text-base font-400 tracking-[0.25px] text-coolgray-20 laptop:text-lg laptop:font-500 laptop:tracking-[0.15px] laptop:text-white">
              {t(
                "TactSuit is a gaming haptic device for feeling sound and action in PC and VR games.",
              )}
            </p>
          </div>

          {/* 두 버튼 모두 글자 폭만큼(hug)이고 **전 구간 가운데 정렬**이다.
              히어로의 제목·본문·화살표가 모두 가운데 축이라 버튼만 좌측 정렬되면 축이 깨진다.

              ⚠️ `justify-center` 를 `laptop:` 에만 걸면 안 된다 — 모바일 기본값이 `start` 라
              버튼이 왼쪽으로 몰린다(실측 390px: 16~324 에 붙고 우측에 50px 남음).
              종전에는 보조 버튼이 `flex-1` 로 남는 폭을 먹어 한 줄을 꽉 채웠기 때문에
              모바일에서 정렬이 드러나지 않았다. hug 로 바꾼 지금은 정렬이 보인다.

              ⚠️ 모바일 좌우 패딩만 16 이다(데스크톱 24). 같은 토큰으로 실제 렌더하면 영문이
              360 폭에서 넘치기 때문이다(시안 130+8+190=328 vs 실제 141+8+195=344).

              ⚠️ **길이 기준은 영문이 아니다.** 보조 버튼 문구 길이:
                fr `Vérifier les jeux pris en charge`(32) · de `Unterstützte Spiele überprüfen`(30)
                es `Comprobar juegos compatibles`(28) · en `Check Supported Games`(21)
              영문보다 30~50% 길다. 그래서 `flex-wrap` 이 필수고, **패딩을 24 로 되돌리면
              그 세 로케일이 먼저 깨진다** — "영문이 들어가니 괜찮다" 로 판단하면 안 된다.
              실측: de·es 는 390 에서도 2줄, fr 은 390 에서 1줄(여유 1px). 전부 넘침 없음이고
              줄바꿈되어도 각 줄이 독립적으로 가운데 정렬된다. */}
          <div className="flex w-full flex-wrap justify-center gap-2 laptop:w-auto laptop:gap-3">
            <Button
              asChild
              variant="blue-fill"
              shape="pill"
              size="sm"
              className="shrink-0 px-4 laptop:px-6"
            >
              <Link href="/tactsuit">{t("Explore TactSuit")}</Link>
            </Button>
            {/* ★**같은 페이지 `#usecase` 로 내려간다** (#5579).

                종전 목적지는 `/games` 였는데 개편으로 그 화면(`GameHome`)이 안 쓰인다
                (#5544 — *"게임전체 페이지가 미제공"*). 대신 **이 문서 안** 「Feel the Game
                Across Every Way You Play」 섹션으로 보낸다.

                ⚠️ `#usecase` 는 새로 만든 앵커가 아니라 **이미 있는 것**이다 —
                `PlayEveryWay.tsx:296` 의 `<section id="usecase">` 이고, 바로 아래 스크롤
                힌트 화살표가 같은 자리를 쓴다. 그래서 앵커·핸들러를 공유한다.

                ⚠️ `next/link` 가 아니라 평범한 `<a>` 다 — 이유는 아래 화살표 주석과 같다
                (`Link href="#usecase"` 는 `/#usecase` 로 렌더돼 폴백이 홈 재이동이 된다). */}
            <Button
              asChild
              variant="white-outline"
              shape="pill"
              size="sm"
              className="shrink-0 px-4 laptop:px-6"
            >
              <a href="#usecase" onClick={scrollToUsecase}>
                {t("Check Supported Games")}
              </a>
            </Button>
          </div>
        </div>

        {/* 스크롤 힌트. 모바일 시안에는 없다(공유 HTML 도 `.hero-stack` gap 만 줄고 화살표는 유지)
            — 여기서는 데스크톱과 동일하게 유지하되 바닥 여백만 줄어든다.

            ⚠️ `href` 는 남겨 둔다 — JS 실패·새 탭 열기 같은 경우의 폴백이고, 링크로서의
            의미(어디로 가는지)도 여기서 나온다. 부드러운 이동만 JS 로 가로챈다.

            ⚠️ `next/link` 가 아니라 평범한 `<a>` 다. `Link href="#usecase"` 는 현재 라우트에
            붙어 `/#usecase` 로 렌더돼, 폴백 동작이 **같은 문서 내 이동이 아니라 홈으로 재이동**이
            된다. 같은 페이지 해시는 앵커 그대로 두는 게 맞다. */}
        <a
          href="#usecase"
          onClick={scrollToUsecase}
          aria-label={t("Scroll down")}
          className="grid size-12 shrink-0 place-items-center text-white transition-opacity hover:opacity-70 motion-safe:animate-bounce"
        >
          <svg
            width="21.37"
            height="12.04"
            viewBox="0 0 21.3677 12.0425"
            fill="none"
            aria-hidden
          >
            <path
              d="M10.6792 12.0425C10.4382 12.0425 10.2139 12.004 10.0062 11.927C9.79854 11.85 9.60104 11.718 9.4137 11.531L0.425203 2.5425C0.148537 2.2655 0.00686993 1.91733 0.000203266 1.498C-0.00613007 1.079 0.135537 0.7245 0.425203 0.4345C0.715203 0.144833 1.06654 0 1.4792 0C1.89187 0 2.2432 0.144833 2.5332 0.4345L10.6792 8.581L18.8252 0.4345C19.1022 0.157833 19.4504 0.0161668 19.8697 0.00950012C20.2887 0.00316679 20.6432 0.144833 20.9332 0.4345C21.2229 0.7245 21.3677 1.07583 21.3677 1.4885C21.3677 1.90117 21.2229 2.2525 20.9332 2.5425L11.9447 11.531C11.7574 11.718 11.5599 11.85 11.3522 11.927C11.1445 12.004 10.9202 12.0425 10.6792 12.0425Z"
              fill="currentColor"
            />
          </svg>
        </a>
      </div>
    </section>
  );
}
